import type { OrderSummaryDto } from '@deli/contracts';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { ORDER_PAGE, useCreateOrder, useOrders, type OrderFilter } from '../api/orders.js';
import { Can } from '../auth/session.js';
import { ErrorText } from '../components/ErrorText.js';
import { Modal } from '../components/Modal.js';
import { Pager } from '../components/Pager.js';
import { QueryState } from '../components/QueryState.js';
import { formatDateTime, formatMoney } from '../format.js';
import { t } from '../i18n/uk.js';
import { CustomerPicker, type ChosenCustomer } from './PricingParts.js';

const to = t.orders;

const GOOD_STATUSES = ['confirmed', 'shipped', 'delivered', 'completed'];

export const orderStatusBadge = (status: OrderSummaryDto['status']) => (
  <span
    className={`badge ${GOOD_STATUSES.includes(status) ? 'ok' : status === 'cancelled' || status === 'returned' ? 'bad' : ''}`}
  >
    {to.statuses[status]}
  </span>
);

export const paymentBadge = (status: OrderSummaryDto['paymentStatus']) => (
  <span className={`badge ${status === 'paid' ? 'ok' : status === 'overpaid' ? 'bad' : ''}`}>
    {to.paymentStatuses[status]}
  </span>
);

/** Choose a customer, create the draft, open it. */
function NewOrderDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateOrder();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<ChosenCustomer | null>(null);
  return (
    <Modal title={to.newOrder} onClose={onClose}>
      <p className="muted">{to.pickCustomer}</p>
      {customer ? (
        <p>
          <strong>{customer.fullName}</strong>{' '}
          <span className="muted">{t.customers.types[customer.type]}</span>{' '}
          <button type="button" onClick={() => setCustomer(null)}>
            {t.pricing.otherCustomer}
          </button>
        </p>
      ) : (
        <CustomerPicker onPick={setCustomer} />
      )}
      <div className="actions">
        <button
          type="button"
          className="primary"
          disabled={!customer || create.isPending}
          onClick={() =>
            customer &&
            create.mutate(
              { customerId: customer.id },
              { onSuccess: (order) => void navigate(`/orders/${order.id}`) },
            )
          }
        >
          {to.create}
        </button>
        <button type="button" onClick={onClose}>
          {t.common.cancel}
        </button>
      </div>
      <ErrorText error={create.error} />
    </Modal>
  );
}

export function OrdersTable({
  orders,
  showCustomer = true,
}: {
  orders: OrderSummaryDto[];
  showCustomer?: boolean;
}) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>{to.number}</th>
          <th>{to.date}</th>
          {showCustomer && <th>{to.customer}</th>}
          <th>{to.status}</th>
          <th>{to.paymentStatus}</th>
          <th>{to.paymentMethod}</th>
          <th className="num">{to.total}</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((o) => (
          <tr key={o.id}>
            <td>
              <Link to={`/orders/${o.id}`}>{o.number}</Link>
              {o.channel === 'woo' && (
                <span className="badge" title={`${to.wooOrder} №${o.wooOrderId}`}>
                  {to.channels.woo}
                </span>
              )}
            </td>
            <td>{formatDateTime(o.placedAt ?? o.createdAt)}</td>
            {showCustomer && (
              <td>
                {o.customerName} <span className="muted">{t.customers.types[o.customerType]}</span>
              </td>
            )}
            <td>
              {orderStatusBadge(o.status)}
              {o.attention.length > 0 && (
                <>
                  {' '}
                  <span className="badge bad" title={o.attention.join('\n')}>
                    {to.attentionBadge}
                  </span>
                </>
              )}
            </td>
            <td>{o.status === 'draft' ? '—' : paymentBadge(o.paymentStatus)}</td>
            <td>{o.paymentMethod ? to.methods[o.paymentMethod] : '—'}</td>
            <td className="num">{formatMoney(o.total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const VIEWS = ['all', 'awaiting_payment', 'to_ship', 'cod_unsettled'] as const;

/** Orders list (ORD-13): saved views, filters, a new draft in a popup. */
export function OrdersPage() {
  const [view, setView] = useState<(typeof VIEWS)[number]>('all');
  const [form, setForm] = useState({
    search: '',
    status: '',
    paymentStatus: '',
    paymentMethod: '',
    customerType: '',
    from: '',
    to: '',
  });
  const [filter, setFilter] = useState<OrderFilter>({});
  const [offset, setOffset] = useState(0);
  const [creating, setCreating] = useState(false);
  const orders = useOrders({ ...filter, view: view === 'all' ? undefined : view, offset });
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setOffset(0);
    setFilter(form);
  };

  return (
    <section className="wide">
      <div className="page-header">
        <h1>{to.title}</h1>
        <Can permission="orders.edit">
          <button type="button" className="primary" onClick={() => setCreating(true)}>
            {to.newOrder}
          </button>
        </Can>
      </div>
      <div className="tabs" role="tablist">
        {VIEWS.map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            className={view === v ? 'active' : ''}
            onClick={() => {
              setView(v);
              setOffset(0);
            }}
          >
            {to.views[v]}
          </button>
        ))}
      </div>
      <form className="inline-form card" onSubmit={submit}>
        <input
          aria-label={t.common.search}
          placeholder={to.searchPlaceholder}
          value={form.search}
          onChange={set('search')}
          className="grow"
        />
        <label>
          {to.status}
          <select
            aria-label={to.status}
            value={form.status}
            onChange={set('status')}
            disabled={view !== 'all'}
          >
            <option value="">{t.common.all}</option>
            {Object.entries(to.statuses).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          {to.paymentStatus}
          <select
            aria-label={to.paymentStatus}
            value={form.paymentStatus}
            onChange={set('paymentStatus')}
            disabled={view !== 'all'}
          >
            <option value="">{t.common.all}</option>
            {Object.entries(to.paymentStatuses).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          {to.paymentMethod}
          <select
            aria-label={to.paymentMethod}
            value={form.paymentMethod}
            onChange={set('paymentMethod')}
          >
            <option value="">{t.common.all}</option>
            {Object.entries(to.methods).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t.customers.type}
          <select
            aria-label={t.customers.type}
            value={form.customerType}
            onChange={set('customerType')}
          >
            <option value="">{t.common.all}</option>
            <option value="b2c">B2C</option>
            <option value="b2b">B2B</option>
          </select>
        </label>
        <label>
          {to.from}
          <input aria-label={to.from} type="date" value={form.from} onChange={set('from')} />
        </label>
        <label>
          {to.to}
          <input aria-label={to.to} type="date" value={form.to} onChange={set('to')} />
        </label>
        <button type="submit">{t.common.search}</button>
      </form>
      <QueryState query={orders}>
        {(page) =>
          page.items.length === 0 ? (
            <p className="muted">{to.empty}</p>
          ) : (
            <>
              <OrdersTable orders={page.items} />
              <Pager
                offset={offset}
                pageSize={ORDER_PAGE}
                total={page.total}
                onChange={setOffset}
              />
            </>
          )
        }
      </QueryState>
      {creating && <NewOrderDialog onClose={() => setCreating(false)} />}
    </section>
  );
}
